import { getServiceEoiRepairDescription } from "../serviceEoiDetails";
import { resolveOrderEoiDetails } from "../serviceOrderWorkflow";
import { displayFileNameFromAssetRef } from "./assetRef";
import {
  buildPassportField,
  buildPassportFieldMeta,
  buildPassportRootKey,
} from "./buildRemanufacturedProductPassport";
import {
  EOI_UPDATABLE_PARAMETER_KEYS,
  formatParameterValueDisplay,
  isEoiParameterUpdateComplete,
} from "./catalogEoiParameters";
import {
  getLatestEoiParameterUpdate,
  isCatalogMilestoneKey,
  isCatalogMilestonePlanComplete,
  listServiceFinalParameterRows,
  normalizeCatalogMilestonesList,
} from "./catalogMilestones";
import { MILESTONE_STATUS } from "./constants";
import {
  getRemanufacturingActivity,
  REMANUFACTURING_OUTCOME_TYPE,
} from "./remanufacturingActivityCatalog";
import { milestonesFromOrder } from "./remanufacturingMilestoneAdapter";

export const SERVICE_PASSPORT_SCHEMA_VERSION = "1.0.0";

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {import("./catalogMilestones").CatalogServiceMilestone[]}
 */
export function catalogMilestonesFromOrder(order) {
  const raw =
    order?.serviceMilestones?.milestones ??
    order?.service_milestones?.milestones ??
    [];
  const rows = Array.isArray(raw) ? raw : [];
  if (
    rows.some((row) =>
      isCatalogMilestoneKey(/** @type {{ key?: string }} */ (row)?.key)
    )
  ) {
    return normalizeCatalogMilestonesList(rows);
  }
  return normalizeCatalogMilestonesList(milestonesFromOrder(order));
}

/**
 * @param {import("../serviceEoiDetails").ServiceEoiDetails} eoiDetails
 * @param {Record<string, import("./buildRemanufacturedProductPassport").PassportField>} fields
 */
function appendEoiDetailsToServicePassport(eoiDetails, fields) {
  const repairDescription = getServiceEoiRepairDescription(eoiDetails);
  if (repairDescription) {
    fields.eoiRepairDescription = buildPassportField(
      repairDescription,
      buildPassportFieldMeta("eoiRepairDescription", "Mandatory", true)
    );
  }

  const machine = eoiDetails.machine;
  if (machine?.tagNumber) {
    fields.eoiMachineTag = buildPassportField(
      machine.tagNumber,
      buildPassportFieldMeta("eoiMachineTag", "Voluntary", true)
    );
  }
  if (machine?.oemBrand) {
    fields.eoiOemBrand = buildPassportField(
      machine.oemBrand,
      buildPassportFieldMeta("eoiOemBrand", "Voluntary", false)
    );
  }
  if (machine?.model) {
    fields.eoiMachineModel = buildPassportField(
      machine.model,
      buildPassportFieldMeta("eoiMachineModel", "Voluntary", false)
    );
  }
  if (machine?.machineCategory) {
    fields.eoiMachineCategory = buildPassportField(
      machine.machineCategory,
      buildPassportFieldMeta("eoiMachineCategory", "Voluntary", false)
    );
  }
  if (machine?.type) {
    fields.eoiMachineType = buildPassportField(
      machine.type,
      buildPassportFieldMeta("eoiMachineType", "Voluntary", false)
    );
  }
  if (machine?.serialNumber) {
    fields.eoiMachineSerialNumber = buildPassportField(
      machine.serialNumber,
      buildPassportFieldMeta("eoiMachineSerialNumber", "Voluntary", true)
    );
  }
  if (machine?.part?.partCategory) {
    fields.eoiPartCategory = buildPassportField(
      machine.part.partCategory,
      buildPassportFieldMeta("eoiPartCategory", "Voluntary", false)
    );
  }

  fields.eoiSubmissionSnapshot = buildPassportField(
    JSON.stringify(eoiDetails),
    buildPassportFieldMeta("eoiSubmissionSnapshot", "Mandatory", true)
  );

  for (const [index, attachment] of (eoiDetails.attachments ?? []).entries()) {
    if (!attachment?.assetRef) continue;
    const fieldId = `eoiAttachment_${index + 1}`;
    fields[fieldId] = buildPassportField(
      JSON.stringify({
        fileName:
          attachment.fileName ??
          displayFileNameFromAssetRef(attachment.assetRef) ??
          "attachment",
        assetRef: attachment.assetRef,
        ...(attachment.category ? { category: attachment.category } : {}),
        ...(attachment.mimeType ? { mimeType: attachment.mimeType } : {}),
      }),
      buildPassportFieldMeta(fieldId, "Voluntary", true)
    );
  }
}

/**
 * @param {Array<{ key?: string, title?: string, inputs?: { outcomeFiles?: Array<{ assetRef?: string, fileName?: string }>, activities?: Array<{ activityId?: string }> } }>} milestones
 */
function collectCatalogMilestoneEvidence(milestones) {
  /** @type {Array<{ milestoneKey: string, milestoneTitle: string, activityId?: string, activityLabel?: string, assetRef: string, fileName: string }>} */
  const rows = [];

  for (const milestone of milestones) {
    if (!isCatalogMilestoneKey(milestone.key)) continue;
    const outcomeFiles = milestone.inputs?.outcomeFiles ?? [];
    for (const file of outcomeFiles) {
      if (!file?.assetRef) continue;
      rows.push({
        milestoneKey: String(milestone.key),
        milestoneTitle: String(milestone.title ?? milestone.key),
        assetRef: file.assetRef,
        fileName:
          file.fileName ??
          displayFileNameFromAssetRef(file.assetRef) ??
          "attachment",
      });
    }

    for (const activityProgress of milestone.inputs?.activities ?? []) {
      const activity = getRemanufacturingActivity(
        String(activityProgress.activityId ?? "")
      );
      if (
        activity?.outcomeType !== REMANUFACTURING_OUTCOME_TYPE.CERTIFICATE &&
        activity?.outcomeType !==
          REMANUFACTURING_OUTCOME_TYPE.PICTURES_AND_VIDEOS
      ) {
        continue;
      }
      rows.push({
        milestoneKey: String(milestone.key),
        milestoneTitle: String(milestone.title ?? milestone.key),
        activityId: activity.id,
        activityLabel: activity.label,
        assetRef: "",
        fileName: "",
      });
    }
  }

  return rows;
}

/**
 * Latest provider-entered values for the four EOI dimension parameters.
 *
 * @param {import("./catalogMilestones").CatalogServiceMilestone[]} allMilestones
 * @param {{ previewOnly?: boolean }} options
 * @returns {Record<string, import("./buildRemanufacturedProductPassport").PassportField>}
 */
export function buildUpdatedPartDimensionFields(allMilestones, options = {}) {
  /** @type {Record<string, import("./buildRemanufacturedProductPassport").PassportField>} */
  const updatedPartDimensions = {};

  for (const parameterKey of EOI_UPDATABLE_PARAMETER_KEYS) {
    const latest = getLatestEoiParameterUpdate(allMilestones, parameterKey);
    if (!latest || !isEoiParameterUpdateComplete(latest)) {
      continue;
    }

    const display = formatParameterValueDisplay(latest);
    if (!display) continue;

    const fieldId = `updated_${parameterKey}`;
    updatedPartDimensions[fieldId] = buildPassportField(
      display,
      buildPassportFieldMeta(
        fieldId,
        options.previewOnly ? "Voluntary" : "Mandatory",
        true
      )
    );
  }

  return updatedPartDimensions;
}

/**
 * @param {import("./catalogMilestones").CatalogServiceMilestone[]} milestones
 */
export function hasServiceDppPreviewMilestones(milestones) {
  return milestones.some(
    (milestone) =>
      milestone.status === MILESTONE_STATUS.COMPLETED ||
      Boolean(milestone.inputs?.serviceDppPreviewGeneratedAt)
  );
}

/**
 * Builds order-scoped service DPP JSON from catalog milestone outputs.
 * @param {Record<string, unknown>|null|undefined} order
 * @param {{ previewOnly?: boolean }} [options]
 */
export function buildServiceDigitalProductPassport(order, options = {}) {
  if (!order) return null;

  const allCatalogMilestones = catalogMilestonesFromOrder(order);
  if (!allCatalogMilestones.length) {
    return null;
  }

  let evidenceMilestones = allCatalogMilestones;
  if (options.previewOnly) {
    evidenceMilestones = allCatalogMilestones.filter(
      (milestone) =>
        milestone.status === MILESTONE_STATUS.COMPLETED ||
        Boolean(milestone.inputs?.serviceDppPreviewGeneratedAt)
    );
    if (!evidenceMilestones.length) {
      return null;
    }
  }

  const productName =
    typeof order.productName === "string" ? order.productName : "Service";
  const rootKey = `${buildPassportRootKey(productName)}Service`;
  const evidenceRows = collectCatalogMilestoneEvidence(evidenceMilestones);
  const workflow =
    order.serviceWorkflow && typeof order.serviceWorkflow === "object"
      ? order.serviceWorkflow
      : null;
  const eoiDetails = resolveOrderEoiDetails(order);

  /** @type {Record<string, import("./buildRemanufacturedProductPassport").PassportField>} */
  const serviceInformation = {
    serviceName: buildPassportField(
      productName,
      buildPassportFieldMeta("serviceName", "Mandatory", true, "ip")
    ),
    orderId: buildPassportField(
      String(order.id ?? ""),
      buildPassportFieldMeta("orderId", "Mandatory", true)
    ),
    catalogVersion: buildPassportField(
      SERVICE_PASSPORT_SCHEMA_VERSION,
      buildPassportFieldMeta("catalogVersion", "Mandatory", false)
    ),
    milestoneCount: buildPassportField(
      String(evidenceMilestones.length),
      buildPassportFieldMeta("milestoneCount", "Mandatory", false)
    ),
  };

  if (workflow?.quoteAcceptedAt) {
    serviceInformation.quoteAcceptedAt = buildPassportField(
      workflow.quoteAcceptedAt,
      buildPassportFieldMeta("quoteAcceptedAt", "Voluntary", false)
    );
  }

  if (eoiDetails) {
    appendEoiDetailsToServicePassport(eoiDetails, serviceInformation);
  }

  Object.assign(
    serviceInformation,
    buildUpdatedPartDimensionFields(allCatalogMilestones, options)
  );

  evidenceRows.forEach((row, index) => {
    if (!row.assetRef) return;
    const fieldId = `outcome_${index + 1}`;
    const label = row.milestoneTitle
      ? `${row.fileName} (${row.milestoneKey} · ${row.milestoneTitle})`
      : row.fileName;
    serviceInformation[fieldId] = buildPassportField(
      label,
      buildPassportFieldMeta(fieldId, "Voluntary", true, "ip")
    );
  });

  return {
    meta: {
      schemaVersion: SERVICE_PASSPORT_SCHEMA_VERSION,
      generatedAt: new Date().toISOString(),
      source: options.previewOnly
        ? "catalog_milestones_preview"
        : "catalog_milestones",
    },
    object: {
      [rootKey]: {
        serviceInformation,
      },
    },
  };
}

/**
 * @param {unknown} passport
 * @returns {Record<string, import("./buildRemanufacturedProductPassport").PassportField>|null}
 */
function readServiceInformationFromPassport(passport) {
  const normalized = normalizeServiceDigitalProductPassport(passport);
  if (!normalized?.object || typeof normalized.object !== "object") {
    return null;
  }
  const root = Object.values(
    /** @type {Record<string, unknown>} */ (normalized.object)
  )[0];
  if (!root || typeof root !== "object" || Array.isArray(root)) {
    return null;
  }
  const record = /** @type {Record<string, unknown>} */ (root);
  const serviceInformation = record.serviceInformation;
  if (serviceInformation && typeof serviceInformation === "object") {
    return /** @type {Record<string, import("./buildRemanufacturedProductPassport").PassportField>} */ (
      serviceInformation
    );
  }
  return /** @type {Record<string, import("./buildRemanufacturedProductPassport").PassportField>} */ (
    record
  );
}

/**
 * Rows for UI / delivery pack: milestone updates first, then service DPP JSON.
 *
 * @param {Record<string, unknown>|null|undefined} order
 */
export function getServiceUpdatedDimensionDisplayRows(order) {
  const milestones = catalogMilestonesFromOrder(order);
  const eoiDetails = resolveOrderEoiDetails(order);
  const rows = listServiceFinalParameterRows(milestones, eoiDetails);

  const passportSources = [
    readServiceInformationFromPassport(order?.serviceDigitalProductPassport),
    readServiceInformationFromPassport(
      buildServiceDigitalProductPassport(order, {
        previewOnly: !isCatalogMilestonePlanComplete(milestones),
      })
    ),
  ];

  return rows.map((row) => {
    if (row.complete) {
      return row;
    }

    const fieldId = `updated_${row.parameterKey}`;
    for (const serviceInformation of passportSources) {
      const value = serviceInformation?.[fieldId]?.value;
      if (value && String(value).trim()) {
        return {
          ...row,
          complete: true,
          display: String(value).trim(),
        };
      }
    }

    return row;
  });
}

/**
 * Client-side passport for UI: always derived from current milestones so updated
 * dimensions appear after each milestone save, not only from stale API payloads.
 *
 * @param {Record<string, unknown>|null|undefined} order
 */
export function resolveServiceDigitalProductPassportForDisplay(order) {
  const milestones = catalogMilestonesFromOrder(order);
  const planComplete = isCatalogMilestonePlanComplete(milestones);
  const canPreview = hasServiceDppPreviewMilestones(milestones);

  if (!planComplete && !canPreview) {
    return { passport: null, isPreview: false };
  }

  const passport = buildServiceDigitalProductPassport(order, {
    previewOnly: !planComplete,
  });

  return {
    passport,
    isPreview: !planComplete,
  };
}

/**
 * @param {unknown} raw
 */
export function normalizeServiceDigitalProductPassport(raw) {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return /** @type {Record<string, unknown>} */ (raw);
  }
  return null;
}
